export const buildFinalReport = ({ todos = [], roachEntries = [] }) => {

    // =============================
    // SORT + SPLIT PHASES
    // =============================
    const sortedTodos = [...todos].sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );

    const phaseSize = Math.ceil(sortedTodos.length / 3);

    const startPhase = sortedTodos.slice(0, phaseSize);
    const middlePhase = sortedTodos.slice(phaseSize, phaseSize * 2);
    const endPhase = sortedTodos.slice(phaseSize * 2);

    // =============================
    // STATS HELPER
    // =============================
    const getStats = (list = []) => {
        const total = list.length;

        const done = list.filter(t => t.status === "Done").length;
        const cancelled = list.filter(t => t.status === "Cancelled").length;
        const planned = list.filter(t => t.status === "Planned").length;

        return {
            total,
            done,
            planned,
            cancelled,
            productivity: total ? Math.round((done / total) * 100) : 0,
        };
    };

    const startStats = getStats(startPhase);
    const middleStats = getStats(middlePhase);
    const endStats = getStats(endPhase);

    // =============================
    // CORE METRICS
    // =============================
    const productivityGrowth =
        endStats.productivity - startStats.productivity;

    // =============================
    // OBJECTIVES
    // =============================
    const objectiveMap = todos.reduce((acc, t) => {
        const key = t.objective || "Unknown";
        acc[key] = (acc[key] || 0) + 1;
        return acc;
    }, {});

    const dominantObjective =
        Object.entries(objectiveMap).sort((a, b) => b[1] - a[1])[0]?.[0] || "Unknown";

    // =============================
    // TREND (WEEKLY DISTRIBUTION)
    // =============================
    const trend = Array(7).fill(0);

    todos.forEach(t => {
        const d = new Date(t.createdAt).getDay();
        const idx = d === 0 ? 6 : d - 1;

        if (t.status === "Done") trend[idx] += 15;
        if (t.status === "Planned") trend[idx] += 8;
        if (t.status === "Cancelled") trend[idx] -= 5;
    });

    const normalizedTrend = trend.map(v => Math.max(0, Math.min(100, v)));

    const days = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];

    const bestDay = days[normalizedTrend.indexOf(Math.max(...normalizedTrend))];
    const weakDay = days[normalizedTrend.indexOf(Math.min(...normalizedTrend))];

    // =============================
    // ROACH ANALYTICS
    // =============================
    const avgRoachScore = roachEntries.length
        ? Number(
            (
                roachEntries.reduce((a, c) => a + (c.avgScore || 0), 0) /
                roachEntries.length
            ).toFixed(1)
        )
        : 0;

    const firstRoach = roachEntries[0] || null;
    const latestRoach = roachEntries[roachEntries.length - 1] || null;

    // =============================
    // SIGNALS
    // =============================
    const focusScore = Math.min(
        100,
        Math.round(
            (((objectiveMap.Build || 0) +
                (objectiveMap.Focus || 0) +
                (objectiveMap.Learning || 0)) /
                Math.max(todos.length, 1)) *
                100
        )
    );

    const consistencyScore = Math.min(
        100,
        Math.round((endStats.done / Math.max(endStats.done + endStats.cancelled, 1)) * 100)
    );

    const disciplineScore = Math.min(
        100,
        Math.round((endStats.done / Math.max(endStats.total, 1)) * 100)
    );

    const resilienceScore = Math.min(
        100,
        Math.round((avgRoachScore / 9) * 100)
    );

    // =============================
    // INSIGHTS HELPERS
    // =============================
    const insights = [
        productivityGrowth >= 0
            ? `Productivity improved by ${productivityGrowth}% during the cycle.`
            : `Productivity dropped by ${Math.abs(productivityGrowth)}% during the cycle.`,

        `Behavior evolved from ${firstRoach?.type || "Unknown"} to ${latestRoach?.type || "Unknown"}`,

        `Most active objective category: ${dominantObjective}`,
    ];

    // =============================
    // WEAKNESSES
    // =============================
    const weaknesses = [];

    if ((objectiveMap.Random || 0) >= 10) weaknesses.push("Too much scattered execution");
    if (focusScore < 40) weaknesses.push("Low focus consistency");
    if (disciplineScore < 50) weaknesses.push("Discipline instability");

    if (!weaknesses.length) weaknesses.push("No major weaknesses detected");

    // =============================
    // STRENGTHS
    // =============================
    const strengths = [];

    if (productivityGrowth >= 15) strengths.push("Execution improved significantly");
    if (focusScore >= 60) strengths.push("Strong deep-work orientation");
    if (resilienceScore >= 60) strengths.push("Stable behavioral resilience");
    if (endStats.cancelled < startStats.cancelled)
        strengths.push("Reduced cancellations over time");

    // =============================
    // ACHIEVEMENTS
    // =============================
    const achievements = [];

    if (todos.length >= 50)
        achievements.push({
            icon: "🔥",
            title: "Execution Builder",
            description: "50+ tasks completed",
        });

    if (consistencyScore >= 75)
        achievements.push({
            icon: "⚡",
            title: "Consistency Driver",
            description: "Strong execution rhythm",
        });

    if (focusScore >= 60)
        achievements.push({
            icon: "🎯",
            title: "Focus Builder",
            description: "Improved deep-work focus",
        });

    // =============================
    // FINAL GRADE
    // =============================
    let finalGrade = "C";

    if (consistencyScore >= 80 && focusScore >= 70) finalGrade = "A";
    else if (consistencyScore >= 65) finalGrade = "B";

    // =============================
    // JOURNEY STAGE
    // =============================
    let journeyStage = "Needs Momentum";

    if (productivityGrowth >= 20) journeyStage = "High Performer";
    else if (productivityGrowth >= 5) journeyStage = "Consistent Builder";

    // =============================
    // RETURN FINAL REPORT
    // =============================
    return {
        completed: true,

        overview: {
            totalDays: 90,
            totalTasks: todos.length,
            completedTasks: endStats.done,
            productivityGrowth,
            finalGrade,
            journeyStage,
        },

        stats: {
            total: todos.length,
            done: todos.filter(t => t.status === "Done").length,
            planned: todos.filter(t => t.status === "Planned").length,
            cancelled: todos.filter(t => t.status === "Cancelled").length,
        },

        signals: {
            focus: focusScore,
            consistency: consistencyScore,
            discipline: disciplineScore,
            resilience: resilienceScore,
        },

        trend: normalizedTrend,
        bestDay,
        weakDay,

        behavior: {
            startType: firstRoach?.type || "Unknown",
            finalType: latestRoach?.type || "Unknown",
            roachScore: avgRoachScore,
        },

        objectives: {
            dominant: dominantObjective,
            distribution: objectiveMap,
        },

        achievements,
        strengths,
        weaknesses,
        insights,

        comparison: {
            start: startStats,
            middle: middleStats,
            end: endStats,
        },
    };
};